/**
 * File: StatCard.tsx
 * Path: apps/admin/src/shared/components/display/StatCard.tsx
 * Description: Industrial-themed KPI card with HUD-style typography, subtle hover interactions, and an integrated skeleton loading state.
 */

import React from "react";
import { cn } from "@idk/utils";

interface StatCardProps {
  label: string;
  value: string | number;
  trend?: string;
  className?: string;
  isLoading?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  trend,
  className,
  isLoading = false,
}) => {
  return (
    <div
      className={cn(
        "relative group overflow-hidden rounded-none border border-border/60 bg-card p-gutter-md shadow-sm transition-all duration-300 hover:border-primary/40 hover:bg-foreground/[0.02]",
        className,
      )}
    >
      {/* Subtle background glow that activates on hover */}
      <div className="absolute -left-12 -top-12 h-32 w-32 rounded-none bg-primary/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

      <div className="relative z-10">
        {isLoading ? (
          <div className="h-3 w-24 animate-pulse rounded-none bg-muted-foreground/20 tracking-wider" />
        ) : (
          <p className="text-xs uppercase tracking-wider text-muted-foreground transition-colors duration-300 group-hover:text-primary">
            {label}
          </p>
        )}

        <div className="mt-stack-md flex items-baseline gap-stack-sm">
          {isLoading ? (
            <>
              <div className="h-10 w-28 animate-pulse rounded-none bg-muted-foreground/20" />
              {trend && (
                <div className="h-4 w-12 animate-pulse rounded-none bg-primary/20" />
              )}
            </>
          ) : (
            <>
              <p className="text-4xl md:text-5xl tracking-tighter text-foreground">
                {value}
              </p>

              {trend && (
                <span className="text-xs uppercase tracking-wider text-primary">
                  {trend}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
